import { useEffect, useMemo, useState, useRef, useCallback, memo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { LineChart } from "@/components/ui/tremor-charts/LineChart";
import { getDataGraph } from "../apiService";
import { getAvailableIntervals, formatGraphDateLabel } from "@/utils/dateUtils";
import axios from "axios";
import { DataGraphResponse } from "../types";
import ReportLoader from "./ReportLoader";
import { useGlobalStore } from "@/GlobalStore";
import { toast } from "sonner";
import ToastContent from "@/components/common/toast/ToastContent";
import { LuCircleCheck } from "react-icons/lu";
import { ColumnOption, SectionConfigWithMetadata } from "@/types";
import {
  SqSelect,
  SqSelectContent,
  SqSelectItem,
  SqSelectTrigger,
} from "@/components/squircle-ui/select";
import { BREAKDOWN_SEPARATOR, PRIMARY_COLUMN_JOINER } from "../constants";
import { differenceInDays } from "date-fns";
import Metrics from "@/components/common/metrics";

// Helper function to lighten a hex color
const lightenColor = (hex: string, factor: number): string => {
  // Remove the # if present
  const color = hex.replace("#", "");

  // Parse RGB values
  const r = parseInt(color.substring(0, 2), 16);
  const g = parseInt(color.substring(2, 4), 16);
  const b = parseInt(color.substring(4, 6), 16);

  // Lighten by mixing with white
  const newR = Math.round(r + (255 - r) * factor);
  const newG = Math.round(g + (255 - g) * factor);
  const newB = Math.round(b + (255 - b) * factor);

  // Convert back to hex
  return `#${newR.toString(16).padStart(2, "0")}${newG.toString(16).padStart(2, "0")}${newB.toString(16).padStart(2, "0")}`;
};
import { cache } from "@/services/cache";
import { SqButton } from "@/components/squircle-ui/button";
import { IconDatabase } from "@tabler/icons-react";
import {
  SqTooltip,
  SqTooltipContent,
  SqTooltipProvider,
  SqTooltipTrigger,
} from "@/components/squircle-ui/tooltip";
import { RiArrowDownSLine, RiLineChartFill } from "@remixicon/react";
import { FooterInfo } from "./FooterInfo";

interface CreativePerformanceGraphProps {
  accountId: string | undefined;
  sectionId: string;
  availableMetrics: ColumnOption[];
  initialConfig?: {
    interval: "daily" | "weekly" | "monthly";
    metric?: string | null; // Deprecated: use metrics instead
    metrics?: string[]; // Array of metric keys (only first one used)
  };
  onConfigChange?: (config: {
    interval: "daily" | "weekly" | "monthly";
    metric?: string | null;
    metrics?: string[];
  }) => void;
  configWithReportMetadata?: SectionConfigWithMetadata;
}

export const CreativePerformanceGraph = memo(function CreativePerformanceGraph({
  accountId,
  sectionId,
  availableMetrics,
  initialConfig,
  onConfigChange,
  configWithReportMetadata,
}: CreativePerformanceGraphProps) {
  const selectedAccount = useGlobalStore((state) => state.selectedAccount);
  const startDate = useMemo(
    () => new Date(configWithReportMetadata?.date?.start_date || ""),
    [configWithReportMetadata?.date?.start_date],
  );
  const endDate = useMemo(
    () => new Date(configWithReportMetadata?.date?.end_date || ""),
    [configWithReportMetadata?.date?.end_date],
  );

  const availableIntervals = useMemo(
    () => getAvailableIntervals(startDate, endDate),
    [startDate, endDate],
  );

  // Date comparison logic for previous period support
  const selectedDateDays = useMemo(() => {
    if (
      !configWithReportMetadata?.date?.start_date ||
      !configWithReportMetadata?.date?.end_date
    ) {
      return 0;
    }
    return differenceInDays(
      new Date(configWithReportMetadata.date.end_date),
      new Date(configWithReportMetadata.date.start_date),
    );
  }, [
    configWithReportMetadata?.date?.start_date,
    configWithReportMetadata?.date?.end_date,
  ]);

  const compareDateDays = useMemo(() => {
    if (
      !configWithReportMetadata?.compare_date?.start_date ||
      !configWithReportMetadata?.compare_date?.end_date
    ) {
      return 0;
    }
    return differenceInDays(
      new Date(configWithReportMetadata.compare_date.end_date),
      new Date(configWithReportMetadata.compare_date.start_date),
    );
  }, [
    configWithReportMetadata?.compare_date?.start_date,
    configWithReportMetadata?.compare_date?.end_date,
  ]);

  const isSamePeriod = compareDateDays === selectedDateDays;
  // Check if comparison is enabled (has compare date) but periods are different lengths
  const hasCompareDate = !!(
    configWithReportMetadata?.compare_date?.start_date &&
    configWithReportMetadata?.compare_date?.end_date
  );
  const showPeriodMismatchWarning = hasCompareDate && !isSamePeriod;

  const [selectedMetric, setSelectedMetric] = useState<string | null>(() => {
    // Support new multi-metric format first (use first metric)
    if (initialConfig?.metrics && initialConfig.metrics.length > 0) {
      const firstMetric = initialConfig.metrics[0];
      if (availableMetrics.some((metric) => metric.key === firstMetric)) {
        return firstMetric;
      }
    }
    // Fallback to old single metric format for backward compatibility
    if (
      initialConfig?.metric &&
      availableMetrics.some((metric) => metric.key === initialConfig.metric)
    ) {
      return initialConfig.metric;
    }
    // Fall back to first available metric
    return availableMetrics[0]?.key;
  });

  const [selectedInterval, setSelectedInterval] = useState<
    "daily" | "weekly" | "monthly"
  >(() => {
    // Check if initial interval is available
    if (initialConfig?.interval && availableIntervals[initialConfig.interval]) {
      return initialConfig.interval;
    }
    // Fall back to weekly if available, otherwise daily
    return availableIntervals.weekly ? "weekly" : "daily";
  });

  const [graphData, setGraphData] = useState<DataGraphResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const visibleAdNames = useGlobalStore(
    (state) => state.visibleAdNamesBySection[sectionId] || [],
  );

  const FOOTER_DISMISSED_KEY = "creative-performance-graph-footer-dismissed";
  const PERIOD_MISMATCH_WARNING_KEY =
    "creative-performance-graph-period-mismatch-warning-dismissed";

  // Use a ref to track the last emitted configuration
  const lastEmittedConfig = useRef<{
    interval: "daily" | "weekly" | "monthly";
    metric?: string | null;
    metrics?: string[];
  } | null>(initialConfig ? { ...initialConfig } : null);

  // Single timeout reference
  const pollingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMounted = useRef(true);
  const prevAvailableMetricsLength = useRef<number>(availableMetrics.length);
  const prevConfigRef = useRef<SectionConfigWithMetadata | null>(null);
  const cancelTokenSourceRef = useRef<ReturnType<typeof axios.CancelToken.source> | null>(
    null,
  );

  // Cleanup effect
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }
    };
  }, []);

  // Clear polling timeout helper
  const clearPollingTimeout = useCallback(() => {
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
    }
  }, []);

  // Update configuration when selections change
  useEffect(() => {
    const newConfig = {
      interval: selectedInterval,
      metrics: selectedMetric ? [selectedMetric] : [],
      metric: selectedMetric, // Keep for backward compatibility
    };

    // Only emit if the configuration has actually changed
    const lastMetric =
      lastEmittedConfig.current?.metrics && lastEmittedConfig.current.metrics.length > 0
        ? lastEmittedConfig.current.metrics[0]
        : lastEmittedConfig.current?.metric;
    if (
      !lastEmittedConfig.current ||
      lastEmittedConfig.current.interval !== newConfig.interval ||
      lastMetric !== selectedMetric
    ) {
      lastEmittedConfig.current = newConfig;
      onConfigChange?.(newConfig);
    }
  }, [selectedInterval, selectedMetric, onConfigChange]);

  // Update state if initialConfig changes significantly
  useEffect(() => {
    if (initialConfig) {
      // Get the metric to compare (from metrics array or single metric)
      const currentMetric =
        initialConfig.metrics && initialConfig.metrics.length > 0
          ? initialConfig.metrics[0]
          : initialConfig.metric;
      const lastMetric =
        lastEmittedConfig.current?.metrics && lastEmittedConfig.current.metrics.length > 0
          ? lastEmittedConfig.current.metrics[0]
          : lastEmittedConfig.current?.metric;

      const configChanged =
        lastEmittedConfig.current?.interval !== initialConfig.interval ||
        lastMetric !== currentMetric;

      if (configChanged) {
        // Validate interval and metric before updating
        if (availableIntervals[initialConfig.interval]) {
          setSelectedInterval(initialConfig.interval);
        }
        if (
          currentMetric &&
          availableMetrics.some((metric) => metric.key === currentMetric)
        ) {
          setSelectedMetric(currentMetric);
        }
        lastEmittedConfig.current = initialConfig;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialConfig]);

  useEffect(() => {
    if (!availableMetrics.some((metric) => metric.key === selectedMetric)) {
      const newMetric = availableMetrics[0];
      if (newMetric) {
        toast.message(
          <ToastContent
            icon={LuCircleCheck}
            headingPrefix="Switched graph metric to"
            headingHighlight={`"${newMetric.label}"`}
            subheadingPrefix={selectedMetric ? "Previous metric has been removed." : ""}
          />,
        );
        setSelectedMetric(newMetric.key);
      } else {
        setSelectedMetric(null);
        setGraphData(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableMetrics.length]);

  useEffect(() => {
    // Helper function to compare configs excluding additional_config
    const configsEqualExcludingAdditionalConfig = (
      prev: SectionConfigWithMetadata | null,
      current: SectionConfigWithMetadata | null,
    ): boolean => {
      if (!prev || !current) return prev === current;

      // Create copies without additional_config for comparison
      const prevWithoutAdditionalConfig = {
        ...prev,
        additional_config: undefined,
      };
      const currentWithoutAdditionalConfig = {
        ...current,
        additional_config: undefined,
      };

      return (
        JSON.stringify(prevWithoutAdditionalConfig) ===
        JSON.stringify(currentWithoutAdditionalConfig)
      );
    };

    // Check if only additional_config changed
    const additionalConfigChanged =
      JSON.stringify(prevConfigRef.current?.additional_config) !==
      JSON.stringify(configWithReportMetadata?.additional_config);

    const onlyAdditionalConfigChanged =
      prevConfigRef.current &&
      configWithReportMetadata &&
      configsEqualExcludingAdditionalConfig(
        prevConfigRef.current,
        configWithReportMetadata,
      ) &&
      additionalConfigChanged;

    // Update ref for next comparison
    prevConfigRef.current = configWithReportMetadata ?? null;

    // Skip fetch if only additional_config changed
    if (onlyAdditionalConfigChanged) {
      return () => {
        // No cleanup needed if we didn't fetch
      };
    }

    // Cancel any existing request before starting a new one
    if (cancelTokenSourceRef.current) {
      cancelTokenSourceRef.current.cancel("Request cancelled due to new request");
    }

    // Clear any existing polling timeout before starting a new request
    clearPollingTimeout();

    // Create new cancel token source
    const cancelTokenSource = axios.CancelToken.source();
    cancelTokenSourceRef.current = cancelTokenSource;

    const fetchGraphData = async (isPolling?: boolean) => {
      if (!accountId || sectionId.startsWith("__temp-")) return;

      // Generate cache key
      const cacheKey = cache.generateKey(
        `/creative/${accountId}/data-graph/${sectionId}/`,
        {
          interval: selectedInterval,
          metadata: configWithReportMetadata,
          availableMetrics: availableMetrics.map((metric) => metric.key),
        },
        accountId,
      );

      // Try to get from cache first (unless polling)
      let hasCachedData = false;
      if (!isPolling) {
        setLoading(true);
        try {
          const cachedData = await cache.get<DataGraphResponse>(cacheKey);
          if (cachedData) {
            setGraphData(cachedData);
            setLoading(false);
            hasCachedData = true;
          }
        } catch (error) {
          console.warn("Failed to get data from cache:", error);
        }
      }

      try {
        // Always fetch fresh data from API
        const response = await getDataGraph(
          accountId,
          sectionId,
          selectedInterval,
          configWithReportMetadata || {},
          cancelTokenSource.token,
          true,
        );

        // If component unmounted during request, don't proceed
        if (!isMounted.current) return;

        // Cache the response (only if not pending)
        if (response?.status !== "pending") {
          try {
            await cache.set(cacheKey, response, 24 * 60 * 60 * 1000); // 24 hours TTL
          } catch (error) {
            console.warn("Failed to cache data:", error);
          }
        }

        // Check response status and set up polling if needed
        if (response?.status === "pending") {
          // Set the data even while pending
          setGraphData(response);

          // Set up next polling iteration
          pollingTimeoutRef.current = setTimeout(() => {
            void fetchGraphData(true);
          }, 5000);
        } else {
          // Only update UI with fresh data if we didn't have cached data
          // This prevents flicker when cached data is already showing
          if (!hasCachedData) {
            setGraphData(response);
            setLoading(false);
          }
          clearPollingTimeout();
        }
      } catch (error) {
        if (!axios.isCancel(error)) {
          console.error("Error fetching graph data:", error);
          setLoading(false);
          clearPollingTimeout();
        }
      }
    };

    const isPolling = prevAvailableMetricsLength.current > availableMetrics.length;
    void fetchGraphData(isPolling);
    prevAvailableMetricsLength.current = availableMetrics.length;

    return () => {
      // Cleanup is handled before making new requests
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    accountId,
    sectionId,
    selectedInterval,
    availableMetrics.length,
    configWithReportMetadata,
  ]);

  // Helper function to format date labels
  const formatDataPointLabel = useCallback(
    (
      dp: { x: string },
      intervalRangesMap: Map<string, { start_date: string; end_date: string }>,
    ) => {
      const intervalRange = intervalRangesMap.get(dp.x);
      return intervalRange
        ? formatGraphDateLabel(
            intervalRange.start_date,
            intervalRange.end_date,
            selectedInterval,
            selectedAccount?.time_zone,
          )
        : dp.x;
    },
    [selectedInterval, selectedAccount?.time_zone],
  );

  // Memoize the maps for better performance
  const intervalRangesMap = useMemo(
    () =>
      new Map(
        graphData?.data?.interval_ranges?.map((range) => [range.label, range]) || [],
      ),
    [graphData?.data?.interval_ranges],
  );

  const previousDataMap = useMemo(
    () => new Map(graphData?.data?.data_points_previous?.map((dp) => [dp.x, dp]) || []),
    [graphData?.data?.data_points_previous],
  );

  // Filter data points to only include visible ad names and handle previous period data
  const filteredGraphData = useMemo(() => {
    if (!graphData?.data?.data_points || !visibleAdNames?.length || !selectedMetric)
      return graphData;

    const processedDataPoints = graphData.data.data_points.map((dataPoint) => {
      const customLabel = formatDataPointLabel(dataPoint, intervalRangesMap);
      const filteredPoint: { [key: string]: string | number } = { x: customLabel };

      // Helper function to process data entries for current or previous period
      const processDataEntries = (entries: [string, unknown][], suffix: string) => {
        visibleAdNames.forEach((adName) => {
          entries.forEach(([key, value]) => {
            const _key = key.replace(BREAKDOWN_SEPARATOR, PRIMARY_COLUMN_JOINER);
            if (_key !== "x" && _key === adName.fullName) {
              // Check if value is an object (not a string) and has the selectedMetric
              if (
                typeof value === "object" &&
                value !== null &&
                selectedMetric in value
              ) {
                // Only add suffix if we have previous period data to compare with
                const label =
                  isSamePeriod && previousDataMap.size > 0
                    ? `${adName.fullName} — ${suffix}`
                    : adName.fullName;
                filteredPoint[label] = (value as Record<string, unknown>)[
                  selectedMetric
                ] as number;
              }
            }
          });
        });
      };

      // Process current period data
      processDataEntries(Object.entries(dataPoint), "Current");

      // Process matching previous period data if periods are the same length
      const matchingPrevious = previousDataMap.get(dataPoint.x);
      if (isSamePeriod && matchingPrevious) {
        processDataEntries(Object.entries(matchingPrevious), "Previous");
      }

      return filteredPoint;
    });

    return {
      ...graphData,
      data: {
        ...graphData.data,
        data_points: processedDataPoints,
      },
    };
  }, [
    graphData,
    visibleAdNames,
    selectedMetric,
    isSamePeriod,
    intervalRangesMap,
    previousDataMap,
    formatDataPointLabel,
  ]);

  const allKeys = useMemo(() => {
    if (!filteredGraphData?.data?.data_points?.length) return [];
    // Collect all unique keys from all data points, excluding 'x'
    const keySet = new Set<string>();
    filteredGraphData.data.data_points.forEach((dataPoint) => {
      Object.keys(dataPoint).forEach((key) => {
        if (key !== "x") keySet.add(key);
      });
    });
    // Order keys to show Current period first, then Previous period for each ad
    const orderedKeys: string[] = [];
    visibleAdNames.forEach((adName) => {
      // Check if we have previous period data to determine naming convention
      const hasPreviousData = keySet.has(`${adName.fullName} — Previous`);

      if (hasPreviousData) {
        // Add Current period key if it exists
        const currentKey = `${adName.fullName} — Current`;
        if (keySet.has(currentKey)) {
          orderedKeys.push(currentKey);
        }
        // Add Previous period key if it exists
        const previousKey = `${adName.fullName} — Previous`;
        if (keySet.has(previousKey)) {
          orderedKeys.push(previousKey);
        }
      } else {
        // No previous data, just use the ad name without suffix
        if (keySet.has(adName.fullName)) {
          orderedKeys.push(adName.fullName);
        }
      }
    });
    // Add any remaining keys that weren't in visibleAdNames
    keySet.forEach((key) => {
      if (!orderedKeys.includes(key)) {
        orderedKeys.push(key);
      }
    });
    return orderedKeys;
  }, [filteredGraphData, visibleAdNames]);

  // Create stroke colors array using section-wise color assignment
  const getAdNameColor = useGlobalStore((state) => state.getAdNameColor);
  const strokeColors = useMemo(() => {
    return allKeys.map((key) => {
      // Extract the ad name from the key (remove " — Current" or " — Previous" suffix)
      const adName = key.replace(/ — (Current|Previous)$/, "");
      const baseColor = getAdNameColor(sectionId, adName) || "#94A3B8"; // Fallback to gray if color not found

      // For previous period data, use a lighter/muted version of the same color
      if (key.includes(" — Previous")) {
        // Convert hex color to a lighter shade by increasing opacity/lightness
        // This creates a visual connection while maintaining differentiation
        return lightenColor(baseColor, 0.6); // 60% lighter for better differentiation
      }
      return baseColor;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allKeys, sectionId]);

  const handleMetricChange = (value: string) => {
    setSelectedMetric(value);
  };

  // Handler for Metrics component - convert ColumnOption[] to string
  const handleMetricsChange = useCallback((selectedMetrics: ColumnOption[]) => {
    if (selectedMetrics.length > 0) {
      handleMetricChange(selectedMetrics[0].key);
    }
  }, []);

  // Handler for selected metrics - convert string to ColumnOption[]
  const getSelectedMetrics = useMemo(() => {
    const selectedMetricObj = availableMetrics.find((m) => m.key === selectedMetric);
    if (!selectedMetricObj) return [];

    return [selectedMetricObj];
  }, [availableMetrics, selectedMetric]);

  // Create value formatter with prefix/suffix from graph data columns
  // First try to get from graph response columns, then fall back to availableMetrics
  const selectedMetricColumn = graphData?.data?.columns?.find(
    (column) => column.key === selectedMetric,
  );
  const selectedMetricData = availableMetrics.find(
    (metric) => metric.key === selectedMetric,
  );
  const valuePrefix = selectedMetricColumn?.prefix || selectedMetricData?.prefix || "";
  const valueSuffix = selectedMetricColumn?.suffix || selectedMetricData?.suffix || "";

  const valueFormatter = useCallback(
    (value: number) => {
      // Use built-in Intl.NumberFormat for compact notation
      const formatter = new Intl.NumberFormat("en-US", {
        notation: "compact",
        compactDisplay: "short",
      });

      const formattedValue = formatter.format(value);

      // No spaces between prefix/suffix and value to prevent Recharts from splitting into multiple tspan elements
      return valuePrefix && valueSuffix
        ? `${valuePrefix}${formattedValue}${valueSuffix}`
        : valuePrefix
          ? `${valuePrefix}${formattedValue}`
          : valueSuffix
            ? `${formattedValue}${valueSuffix}`
            : formattedValue;
    },
    [valuePrefix, valueSuffix],
  );

  return (
    <Card className="mb-2 rounded-[12px] shadow-none pt-[13px] px-3 pb-2 overflow-visible">
      <CardHeader className="p-0 space-y-0 flex flex-row items-center justify-between">
        <div className="flex flex-col">
          <CardTitle>
            <div className="flex items-center gap-2">
              <RiLineChartFill size={16} className="text-brand-primary-600 shrink-0" />
              <h4 className="text-brand-primary-800 overflow-hidden text-ellipsis relative top-[1px] text-[13px] font-medium not-italic leading-none">
                Line chart
              </h4>
            </div>
          </CardTitle>
        </div>
        <div className="flex gap-2">
          <SqSelect
            value={selectedInterval}
            onValueChange={(value: "weekly" | "monthly") => setSelectedInterval(value)}
          >
            <SqSelectTrigger
              radius={6}
              className="h-7 flex items-center gap-[6px] bg-transparent py-1 pl-2.5 pr-2"
            >
              <span className="text-[#67749B] text-ellipsis text-[11px] font-normal leading-[19.157px] tracking-[0.24px]">
                Period
              </span>
              <span className="overflow-hidden [-webkit-box-orient:vertical] [-webkit-line-clamp:1] text-brand-primary-new/80 text-ellipsis text-[11px] font-medium leading-[19.157px] tracking-[0.24px]">
                {selectedInterval.charAt(0).toUpperCase() + selectedInterval.slice(1)}
              </span>
            </SqSelectTrigger>
            <SqSelectContent radius={12}>
              {(["daily", "weekly", "monthly"] as const).map(
                (interval) =>
                  availableIntervals[interval] && (
                    <SqSelectItem
                      key={interval}
                      value={interval}
                      className="overflow-hidden [-webkit-box-orient:vertical] [-webkit-line-clamp:1] text-brand-primary-new/80 text-ellipsis text-[11px] font-normal leading-[19.157px] tracking-[0.24px]"
                    >
                      {interval.charAt(0).toUpperCase() + interval.slice(1)}
                    </SqSelectItem>
                  ),
              )}
            </SqSelectContent>
          </SqSelect>
          <Metrics
            renderingStyle="single_select"
            selectedMetrics={getSelectedMetrics}
            onMetricChange={handleMetricsChange}
            metricsOptions={availableMetrics}
            trigger={
              <SqTooltipProvider>
                <SqTooltip>
                  <SqTooltipTrigger>
                    <SqButton
                      className="flex items-center gap-[6px] h-7 bg-transparent pl-2.5 pr-2 py-1"
                      bgColor="#FFFFFF"
                      hasOutline
                      shadow={"sm"}
                      hoverBgColor="#FBFBFB"
                      radius={6}
                    >
                      <span className="text-[#67749B] text-ellipsis text-[11px] font-normal leading-[19.157px] tracking-[0.24px]">
                        Metric{" "}
                      </span>
                      {getSelectedMetrics?.[0] ? (
                        <div className="flex items-center gap-1 min-w-0 flex-1">
                          {getSelectedMetrics[0]?.sub_type === "EXTERNAL" && (
                            <IconDatabase
                              size={14}
                              className={`text-[#02185999] shrink-0 -translate-y-[0.5px]`}
                            />
                          )}
                          <span className="truncate text-brand-primary-new/80 text-[11px] font-medium leading-[19.157px] tracking-[0.24px] max-w-[160px]">
                            {getSelectedMetrics[0]?.label}
                          </span>
                          {getSelectedMetrics[0]?.show_platforms && (
                            <span className="text-[#FF4500] shrink-0">*</span>
                          )}
                        </div>
                      ) : null}
                      <RiArrowDownSLine size={14} color="#9aa3bd" className="shrink-0" />
                    </SqButton>
                  </SqTooltipTrigger>
                  {getSelectedMetrics[0]?.show_platforms &&
                    getSelectedMetrics[0]?.platforms &&
                    getSelectedMetrics[0]?.platforms.length > 0 && (
                      <SqTooltipContent
                        portal={true}
                        side="bottom"
                        align="start"
                        alignOffset={20}
                      >
                        <p>
                          {getSelectedMetrics[0]?.label}
                          <br />
                          Available for{" "}
                          {getSelectedMetrics[0]?.platforms
                            .map(
                              (platform) =>
                                platform.charAt(0).toUpperCase() +
                                platform.slice(1).toLowerCase(),
                            )
                            .join(", ")}
                        </p>
                      </SqTooltipContent>
                    )}
                </SqTooltip>
              </SqTooltipProvider>
            }
          />
        </div>
      </CardHeader>
      <CardContent className="p-0 mt-[24px]">
        <div className="h-[320px] w-full">
          {!selectedMetric ? (
            <div className="h-[320px] w-full flex items-center justify-center text-gray-500">
              Select a metric
            </div>
          ) : loading ? (
            <ReportLoader message={graphData?.message} showProgressBar={true} />
          ) : (
            <LineChart
              className="h-full w-full"
              data={filteredGraphData?.data?.data_points || []}
              index="x"
              categories={allKeys}
              valueFormatter={valueFormatter}
              showXAxis={true}
              showYAxis={true}
              showGridLines={true}
              showTooltip={true}
              showLegend={allKeys.length <= 4 ? true : false}
              startEndOnly={false}
              strokeColors={strokeColors}
            />
          )}
        </div>
      </CardContent>
      {showPeriodMismatchWarning && (
        <FooterInfo
          description="The current and previous periods must be the same length to compare on chart."
          backgroundColor="#FFF7EB"
          textColor="#FF9D00"
          iconBgColor="white"
          iconColor="#FF9D00"
          showButton={false}
          storageKey={PERIOD_MISMATCH_WARNING_KEY}
        />
      )}
      <FooterInfo
        description="This chart uses only the visible table for clearer comparison."
        backgroundColor="#E7F3FF"
        textColor="#0071D8"
        iconBgColor="white"
        iconColor="#2A94F4"
        borderColor="#C6E3FF"
        buttonText="Okay, got it"
        buttonIconColor="#FF6A33"
        showButton={true}
        storageKey={FOOTER_DISMISSED_KEY}
      />
    </Card>
  );
});
